import React, { useCallback, useEffect, useState } from "react";
import { Tag, WifiOff } from "lucide-react";
import EmptyState from "../components/CommonComponents/EmptyState";
import SearchInput from "../components/CommonComponents/SearchInput";
import Pagination from "../components/CommonComponents/Pagination";
import TableLoadingSkeleton from "../components/CommonComponents/TableLoadingSkeleton";
import { tableContainer, tableBase, tableHead, tableHeader, tableCell, tableRow } from "../utils/tableStyle";
import axiosInstance from "../services/axiosInstance";

const formatCurrency = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return "-";
    return new Intl.NumberFormat("en-PH", { style: "currency", currency: "PHP" }).format(num);
};

const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleDateString();
};

const formatTime = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const ProductPriceLogsPage = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);

    const rowsPerPage = 10;

    const loadLogs = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axiosInstance.get("/admin/products/branch-price-logs", {
                params: {
                    page: currentPage,
                    limit: rowsPerPage,
                    search: searchTerm || undefined,
                },
            });
            setRows(Array.isArray(response?.data?.logs) ? response.data.logs : []);
            setTotalPages(Math.max(Number(response?.data?.totalPages) || 1, 1));
            setTotalCount(Number(response?.data?.total) || 0);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    }, [currentPage, rowsPerPage, searchTerm]);

    useEffect(() => {
        loadLogs();
    }, [loadLogs]);

    return (
        <div className="pt-6">
            <div className="mb-6">
                <h2 className="text-2xl font-bold" style={{ color: "var(--color-text)" }}>Product Price Logs</h2>
                <p className="text-sm text-gray-500">Track branch price changes for products.</p>
            </div>

            <div className="mb-4">
                <SearchInput
                    value={searchTerm}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                    }}
                    placeholder="Search logs by product name..."
                />
            </div>

            {error ? (
                <EmptyState
                    icon={WifiOff}
                    title="Failed to Load Product Logs"
                    message="Check your connection and try again."
                    onRetry={loadLogs}
                    retryLabel="Reload"
                />
            ) : loading ? (
                <div className="rounded-lg border border-gray-200 bg-white overflow-auto max-h-[calc(100vh-340px)] min-h-[300px]">
                    <TableLoadingSkeleton
                        columnLabels={[
                            "Date",
                            "Time",
                            "Product",
                            "Branch",
                            "Old Price",
                            "New Price",
                            "Action",
                            // "User",
                        ]}
                        columnTypes={["text", "text", "text", "text", "text", "text", "text", "text"]}
                        columnTemplate="1fr 0.9fr 1.6fr 1.3fr 1fr 1fr 0.9fr 1.2fr"
                        rowCount={4}
                        headerCellClassName="text-center"
                    />
                </div>
            ) : !rows.length ? (
                <EmptyState
                    icon={Tag}
                    title="No Product Price Logs"
                    message="No pricing changes found for your current filter."
                />
            ) : (
                <>
                    <div className={tableContainer}>
                        <table className={tableBase}>
                            <thead className={tableHead} style={{ background: "var(--color-primary)" }}>
                                <tr>
                                    <th className={tableHeader + " text-center"}>Date</th>
                                    <th className={tableHeader + " text-center"}>Time</th>
                                    <th className={tableHeader + " text-center"}>Product</th>
                                    <th className={tableHeader + " text-center"}>Branch</th>
                                    <th className={tableHeader + " text-center"}>Old Price</th>
                                    <th className={tableHeader + " text-center"}>New Price</th>
                                    <th className={tableHeader + " text-center"}>Action</th>
                                    {/* <th className={tableHeader + " text-center"}>User</th> */}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row) => {
                                    const createdAt = row.created_at || row.createdAt;
                                    const userLabel = row.changedBy?.username || row.changedBy?.email || "-";
                                    const oldBranch = row.oldBranch?.name || "";
                                    const newBranch = row.branch?.name || "";
                                    const branchLabel = oldBranch && newBranch && oldBranch !== newBranch
                                        ? `${oldBranch} → ${newBranch}`
                                        : (newBranch || oldBranch || "-");
                                    return (
                                        <tr key={row.id} className={tableRow + " border-b last:border-0"}>
                                            <td className={tableCell + " text-center"}>{formatDate(createdAt)}</td>
                                            <td className={tableCell + " text-center"}>{formatTime(createdAt)}</td>
                                            <td className={tableCell + " text-center"}>{row.product?.name || "-"}</td>
                                            <td className={tableCell + " text-center"}>{branchLabel}</td>
                                            <td className={tableCell + " text-center"}>{formatCurrency(row.old_price)}</td>
                                            <td className={tableCell + " text-center"}>{formatCurrency(row.new_price)}</td>
                                            <td className={tableCell + " text-center"}>{row.action}</td>
                                            {/* <td className={tableCell + " text-center"}>{userLabel}</td> */}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <div className="flex flex-col items-center gap-2 mt-3">
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                        />
                        <p className="text-sm" style={{ color: "var(--color-text)", opacity: 0.7 }}>
                            Showing {totalCount === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1} -{" "}
                            {Math.min(currentPage * rowsPerPage, totalCount)} of {totalCount} items | Page {currentPage} of {totalPages}
                        </p>
                    </div>
                </>
            )}
        </div>
    );
};

export default ProductPriceLogsPage;
